import { useEffect, useMemo, useRef } from 'react';
import { hashKey, type QueryClient, useQueryClient } from '@tanstack/react-query';
import {
  getShopifyStorefrontCommerceAdapter,
  type AppBootstrapV2,
  type ConciergeCartState,
  type ConciergeCommerceAdapter,
  type ConciergeCommerceProduct,
  type ShoppingCartItem,
} from '@brandclub/common-ui';

import { activeCartsQueryOptions, useCartUpdates } from '@/api/cache/cartQueryOptions';
import { sumAllItemQuantities } from '@/utils';
import BagDrawer from '../BagButton/BagDrawer';

/**
 * Which bag the concierge BUBBLE fills — the whole switch, in one place.
 *
 * ## The rule
 *
 * 1. **Checkout on, and the Brandclub cart is mounted → the Brandclub bag.** On
 *    every surface, the Shopify App Proxy too. Checkout on means "this brand
 *    sells through the Brandclub cart". PetArmor is this case.
 * 2. **Checkout off, on our Shopify theme embed, and the hosting surface is the
 *    Shopify App Proxy → the Shopify storefront cart.**
 * 3. **Otherwise → no bag.** The concierge shows no "Add to bag". The product
 *    panel keeps its plain "View" link instead.
 *
 * Checkout on but the cart NOT mounted gives no bag. It never falls back to the
 * Shopify cart: that is a second bag that the shopper does not pay from.
 *
 * The full-page concierge (landing-page `useConciergeCommerceAdapter`) follows
 * the same rule. Keep the two in step.
 */
export type ConciergeBag = 'brandclub' | 'shopify' | 'none';

export interface ConciergeBagInput {
  /** Retailers the CHECKOUT capability covers. Empty means checkout is off. */
  checkoutRetailerIds: number[];
  /** Whether this page load mounted the Brandclub cart (the bag drawer and its writer). */
  cartEnabled: boolean;
  /** `window.bcStoreKind`. Only our `ask-us` theme app embed sets it, to 'shopify'. */
  storeKind?: string;
  /** The hosting surface from the bootstrap. Absent until the bootstrap loads. */
  servingSurface?: string;
}

export function pickConciergeBag({
  checkoutRetailerIds,
  cartEnabled,
  storeKind,
  servingSurface,
}: ConciergeBagInput): ConciergeBag {
  if (checkoutRetailerIds.length > 0) return cartEnabled ? 'brandclub' : 'none';
  if (storeKind === 'shopify' && servingSurface === 'shopify_app_proxy') return 'shopify';
  return 'none';
}

/**
 * `capabilities.checkout` is a map keyed by `appChannelId`, one row per channel
 * in scope. An empty map means checkout is off.
 */
function checkoutRetailerIds(bootstrap: AppBootstrapV2 | undefined): number[] {
  return Object.values(bootstrap?.capabilities?.checkout ?? {}).map((row) => row.retailerId);
}

type AddItems = (params: { action: 'add'; retailerId: number; items: ShoppingCartItem[] }) => Promise<unknown>;

/** The Brandclub cart line is keyed by the retailer's own sku. */
function toCartItem(product: ConciergeCommerceProduct): ShoppingCartItem {
  return {
    brandId: product.brandId ?? 0,
    retailerSku: product.retailerSku as string,
    retailerId: product.retailerId as number,
    stacklineSku: product.stacklineSku as string,
    quantity: product.quantity ?? 1,
    retailPrice: product.retailPrice ?? 0,
    title: product.title,
    dtcProductPageUrl: product.retailerProductPageUrl ?? product.dtcProductPageUrl,
  };
}

/**
 * The Brandclub bag, as a concierge commerce adapter.
 *
 * It writes through `addItems`, which is this app's own cart mutation
 * (`useCartUpdates`). Every other "Add to bag" in this app uses that mutation.
 * A second write path to the same cart is how two bags drift apart.
 *
 * It needs no product lookup. The panel already has every field a cart line
 * needs.
 */
function createShopAppBagAdapter({
  queryClient,
  addItems,
  getCheckoutRetailerIds,
}: {
  queryClient: QueryClient;
  addItems: AddItems;
  getCheckoutRetailerIds: () => number[];
}): ConciergeCommerceAdapter {
  const cartQueryKey = activeCartsQueryOptions().queryKey;
  const cartQueryHash = hashKey(cartQueryKey);
  const readCount = () => sumAllItemQuantities(queryClient.getQueryData(cartQueryKey)?.userRetailerShoppingCarts ?? []);

  const adapter: ConciergeCommerceAdapter = {
    provider: 'brandclub',

    // The Brandclub checkout owns the order row, so it already knows the shopper.
    // Only a native Shopify checkout needs these cart attributes.
    stampConciergeVisitor: async () => undefined,
    stampConciergeAttribution: async () => undefined,

    canAddToCart(product) {
      if (product.isAvailable === false) return false;
      if (!product.retailerSku || !product.stacklineSku) return false;
      if (typeof product.retailerId !== 'number') return false;
      return getCheckoutRetailerIds().includes(product.retailerId);
    },

    async addToCart(product) {
      if (!adapter.canAddToCart(product)) return { ok: false as const, error: 'not_checkout_eligible' };
      const item = toCartItem(product);
      try {
        // The mutation rejects when the server write fails, and rolls the cache back.
        await addItems({ action: 'add', retailerId: item.retailerId, items: [item] });
      } catch (err) {
        console.error('[conciergeCommerce] add to bag failed:', err);
        return { ok: false as const, error: 'cart_add_failed' };
      }
      return { ok: true as const, itemCount: readCount() };
    },

    getCartState: () => ({ itemCount: readCount() }),

    subscribe(listener: (state: ConciergeCartState) => void) {
      let last = readCount();
      return queryClient.getQueryCache().subscribe((event) => {
        if (event.query.queryHash !== cartQueryHash) return;
        const next = readCount();
        if (next === last) return;
        last = next;
        listener({ itemCount: next });
      });
    },

    refreshCart: async () => {
      try {
        await queryClient.fetchQuery(activeCartsQueryOptions());
      } catch (err) {
        // An unloaded bag shows as empty. The drawer fetches again when it opens.
        console.error('[conciergeCommerce] could not load the bag:', err);
      }
    },

    // The drawer shows the bag and holds the checkout button, so both go there.
    // Navigating away would close the conversation.
    goToCart: () => BagDrawer.show(),
    goToCheckout: () => BagDrawer.show(),
  };

  return adapter;
}

/**
 * The bubble's bag. It returns `undefined` when the bubble has no bag.
 *
 * The bootstrap arrives over HTTP after the first paint. Until it loads, the
 * answer is "no bag". So the bubble never shows the wrong bag first.
 */
export function useConciergeCommerce({
  bootstrap,
  cartEnabled,
}: {
  bootstrap: AppBootstrapV2 | undefined;
  cartEnabled: boolean;
}): ConciergeCommerceAdapter | undefined {
  const queryClient = useQueryClient();
  const { mutateAsync } = useCartUpdates();

  // The adapter outlives renders. These refs let it always use the newest values.
  const addItemsRef = useRef<AddItems>(mutateAsync);
  addItemsRef.current = mutateAsync;
  const retailerIdsRef = useRef<number[]>([]);
  retailerIdsRef.current = checkoutRetailerIds(bootstrap);

  const bag = pickConciergeBag({
    checkoutRetailerIds: retailerIdsRef.current,
    cartEnabled,
    storeKind: typeof window === 'undefined' ? undefined : (window as { bcStoreKind?: string }).bcStoreKind,
    servingSurface: bootstrap?.app?.servingSurface,
  });

  const brandclubAdapter = useMemo(
    () =>
      createShopAppBagAdapter({
        queryClient,
        addItems: (params) => addItemsRef.current(params),
        getCheckoutRetailerIds: () => retailerIdsRef.current,
      }),
    [queryClient],
  );

  // Show the shopper's real item count on the concierge bag chip from the start.
  useEffect(() => {
    if (bag === 'brandclub') void brandclubAdapter.refreshCart();
  }, [bag, brandclubAdapter]);

  if (bag === 'brandclub') return brandclubAdapter;
  if (bag === 'shopify') return getShopifyStorefrontCommerceAdapter();
  return undefined;
}
